import { ColumnsType } from 'antd/es/table'
import { TCommonTableData } from './types'
import { currencyFormatter } from '../ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { LeadByIndicator } from '../../components/LeadByIndicator'
import IconNameItem from '../../components/IconNameItem/IconNameItem'
import { ROLES_NAMES } from '../../constants'
import { Sorter } from '../../components/Sorter/Sorter'
import { SortOrder } from 'antd/es/table/interface'

export const CHARTS_COLORS_TXT = {
  contract_contracting_count: {
    color: '#E1E7ED',
    name: 'Contracting'
  },
  contract_out_for_signature_count: {
    color: '#FFDA96',
    name: 'Out for signature'
  },
  contract_executed_count: {
    color: '#AABFEB',
    name: 'Executed'
  },
  contract_active_count: {
    color: '#C3DD8C',
    name: 'Active'
  },
  contract_redlining_count: {
    color: '#E1E7ED',
    name: 'Redlining'
  },
  contract_upload_in_progress_count: {
    color: '#E1E7ED',
    name: 'Upload in progress'
  },
  rfp_created_count: {
    color: '#C3DD8C',
    name: 'Created'
  },
  rfp_in_cs_queue: {
    color: '#6D747D',
    name: 'In CS queue'
  },
  rfp_vendor_acceptance: {
    color: '#E1E7ED',
    name: 'Vendor acceptance'
  },
  rfp_bidding_qa: {
    color: '#AABFEB',
    name: 'Bidding Q&A'
  },
  rfp_bidding_in_progress: {
    color: '#FFDA96',
    name: 'Bidding in progress'
  },
  rfp_price_files_analysis: {
    color: '#2767A9',
    name: 'Price file analysis'
  },
  rfp_voting_in_progress: {
    color: '#FFDA96',
    name: 'Voting in progress'
  },
  rfp_vendors_selection: {
    color: '#2767A9',
    name: 'Vendor selection'
  },
  rfp_waiting_for_assignment: {
    color: '#FBB8B8',
    name: 'Waiting for assignment'
  },
  empty_state: {
    color: '#E9EEF7',
    name: ''
  }
}

export const columns = (
  canSeeHealthSystem: boolean
): ColumnsType<TCommonTableData> => {
  const tableCols: ColumnsType<TCommonTableData> = [
    {
      dataIndex: 'health_system',
      key: 'health_system',
      width: 204,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter
          dataIndex="health_system"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Health system
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'hospital',
      key: 'hospital',
      width: 204,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter
          dataIndex="hospital"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Hospital
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'led_by',
      key: 'led_by',
      width: 104,
      title: 'Led by',
      sorter: false,
      showSorterTooltip: false,
      render: (_, { led_by }) =>
        led_by ? <LeadByIndicator value={led_by} /> : '-'
    },
    {
      dataIndex: 'contract_category',
      key: 'contract_category',
      width: 296,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter
          dataIndex="contract_category"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Category
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false,
      render: (_, { contract_category }) => contract_category ?? '-'
    },
    {
      dataIndex: 'portfolio',
      key: 'portfolio',
      width: 160,
      title: 'Portfolio',
      sorter: false,
      showSorterTooltip: false,
      render: (_, { portfolio }) => portfolio ?? '-'
    },
    {
      dataIndex: 'expected_spend',
      key: 'expected_spend',
      width: 204,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter
          dataIndex="expected_spend"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Expected spend, $
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false,
      render: (_, { expected_spend }) =>
        expected_spend ? currencyFormatter(expected_spend) : '-'
    },
    {
      dataIndex: 'expected_savings',
      key: 'expected_savings',
      width: 204,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter
          dataIndex="expected_savings"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Expected savings, $
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false,
      render: (_, { expected_savings }) =>
        expected_savings ? currencyFormatter(expected_savings) : '-'
    },
    {
      dataIndex: 'additional_values',
      key: 'additional_values',
      width: 204,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter
          dataIndex="additional_values"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Additional values, $
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false,
      render: (_, { additional_values }) =>
        additional_values ? currencyFormatter(additional_values) : '-'
    },
    {
      dataIndex: 'sourcing_team',
      key: 'sourcing_team',
      width: 264,
      title: 'Sourcing owner',
      sorter: false,
      showSorterTooltip: false,
      render: (_, { sourcing_team }) => {
        if (!sourcing_team) return '–'
        return (
          <IconNameItem
            firstName={sourcing_team.first_name}
            lastName={sourcing_team.last_name}
            src={sourcing_team.avatar?.file_preview || null}
            roleName={ROLES_NAMES[sourcing_team.role]}
          />
        )
      }
    }
  ]

  return canSeeHealthSystem
    ? tableCols
    : tableCols.filter((col) => col.key !== 'health_system')
}
